import { type OlkCoreErrorCodes } from "owa-error-codes";
import { DatapointStatus, PerformanceDatapoint } from "owa-analytics";
import { getApplicationSettings } from "owa-application-settings";
import { fetch } from "owa-fetch";
import { getAccessTokenforResourceAsLazy } from "owa-tokenprovider";
import { trace } from "owa-trace";

import type { TraceErrorObject } from "owa-analytics-types";
import type { MailboxInfo } from "owa-client-types";
import type { PublicError } from "@microsoft/microsoft-graph-types";

type Error = {
    error?: PublicError | null;
};

/**
 * Sends a request to the Microsoft Graph API.
 * @template TResponse The type of the response.
 * @param apiName The name of the API to use for telemetry.
 * @param method The HTTP method to use.
 * @param requestUrl The URL to send the request to. Must be relative to the MSGraph URL and start with a slash.
 * @param payload The payload to send with the request. Will be serialized to JSON.
 * @param mailboxInfo The mailbox used to get the base MSGraph URL and token.
 * @param eventualConsistency Whether to use eventual consistency. Required when using $count, $search, $filter and $orderBy. Defaults to false.
 *                            See https://devblogs.microsoft.com/microsoft365dev/microsoft-graph-advanced-queries-for-directory-objects-are-now-generally-available/
 *                            It is important to note that it can take a few seconds for queries with eventual consistency to return a recently updated value.
 *
 * @returns The response from the API.
 */
export default async function sendMsGraphRequest<TResponse>(mailboxInfo: MailboxInfo, { apiName, method = "GET", requestUrl, payload, eventualConsistency, requestId, requestHeaders = {}, }: {
    apiName: string;
    method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE";
    requestUrl: string;
    payload?: object;
    eventualConsistency?: boolean;
    requestId?: string;
    requestHeaders?: Record<string, string>;
}): Promise<TResponse> {
    const dataPoint = new PerformanceDatapoint("SendMsGraphRequest", mailboxInfo);
    dataPoint.addCustomProperty("apiName", apiName);
    
    const { resourceUrl } = getApplicationSettings("Graph", mailboxInfo);
    const endpoint = new URL(requestUrl, resourceUrl);
    
    try {
        getDatapointIfNotEnded(dataPoint)?.addToCustomWaterfall(1, "token_start");
        const [token, tokenPromise] = getAccessTokenforResourceAsLazy(resourceUrl, "SendMSGraphRequest" /*apiName*/, requestId, undefined /* targetTenantId */, undefined /* wwwAuthenticationHeader */, undefined /* showFullTokenResponse */, undefined /* preferIdpToken */, mailboxInfo);
        
        const Authorization = (token as string) || ((await tokenPromise) as string);
        if (!Authorization) {
            const e: TraceErrorObject = new Error("No token available");
            e.owaErrorCode = "00_0008";
            e.datapointStatus = DatapointStatus.AuthError;
            throw e;
        }
        
        getDatapointIfNotEnded(dataPoint)?.addToCustomWaterfall(2, "token_available");
        
        const headers: Record<string, string> = {
            Authorization,
            "Content-Type": "application/json",
            ...requestHeaders,
        };
        if (eventualConsistency) {
            headers["ConsistencyLevel"] = "eventual";
        }
        
        const body = payload ? JSON.stringify(payload) : undefined;
        const response = await fetch(endpoint, { method, headers, body });
        const contentType = response.headers.get("content-type");
        const data = contentType && contentType.indexOf("application/json") !== -1
            ? ((await response.json()) as TResponse & Error)
            : ({} as TResponse & Error);
        
        if (data?.error) {
            const e: TraceErrorObject = new Error("Graph API error");
            e.additionalInfo = { diagnosticMessage: data.error.message };
            e.networkError = true;
            e.httpStatus = response.status;
            e.response = response;
            (e as any).status = response.status;
            (e as any).code = data.error.code;
            (e as any).innerError = data.error.innerError;
            throw e;
        }
        
        getDatapointIfNotEnded(dataPoint)?.end();
        return data as TResponse;
    }
    catch (e) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'TraceErrorObject | ErrorCodes'.
        // @ts-expect-error
        getDatapointIfNotEnded(dataPoint)?.endWithError(DatapointStatus.ServerError, e);
        trace.info(`MsGraph failed to fetch ${apiName}.`);
        throw e;
    }
}

// The datapoint will end after 60 seconds. If the token takes more time retrieve, the following
// "end" methods of the datapoint will throw an error.
function getDatapointIfNotEnded(dp: PerformanceDatapoint) {
    if (dp.isSent()) {
        return null;
    }
    
    return dp;
}
